// PATH: src/pages/NuestroHorario.jsx
// VERSION: 1.1 — Lista de eventos fijos (recurrentes) + color por evento

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Spinner,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiClock, FiMapPin } from 'react-icons/fi';
import { API } from '../utils/apiBase';

const WEEKDAY_LABELS = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
];

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function normalizeFixedEventsPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

export default function NuestroHorario() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const payload = await fetchJson(`${API}/fixed-events`);
        if (!alive) return;
        setItems(normalizeFixedEventsPayload(payload));
      } catch (e) {
        if (alive) setErr(e?.message || 'Error al cargar horarios');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const grouped = useMemo(() => {
    const byDay = new Map();
    for (const ev of items) {
      if (ev.isActive === false) continue;
      const w = Number(ev.weekday ?? 0);
      if (!byDay.has(w)) byDay.set(w, []);
      byDay.get(w).push(ev);
    }

    const result = [];
    for (let w = 0; w < 7; w++) {
      if (!byDay.has(w)) continue;
      const list = byDay
        .get(w)
        .slice()
        .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
      result.push({ weekday: w, label: WEEKDAY_LABELS[w], items: list });
    }
    return result;
  }, [items]);

  if (loading) {
    return (
      <Box bg="bg.default" flex="1">
        <Container maxW="container.lg" py={{ base: 8, md: 12 }}>
          <HStack justify="center" py={10}>
            <Spinner size="lg" />
            <Text> Cargando horarios…</Text>
          </HStack>
        </Container>
      </Box>
    );
  }

  if (err) {
    return (
      <Box bg="bg.default" flex="1">
        <Container maxW="container.lg" py={{ base: 8, md: 12 }}>
          <Heading size="md" color="red.600">
            Error al cargar horarios
          </Heading>
          <Text color="gray.700" mt={2}>
            {String(err)}
          </Text>
        </Container>
      </Box>
    );
  }

  return (
    <>
      <Container maxW="container.lg" py={{ base: 4, md: 8 }}>
        <Heading textAlign="center" mb={2} color="text.default" size="lg">
          Nuestro Horario
        </Heading>

        {grouped.length === 0 ? (
          <Box
            bg="bg.card"
            borderRadius="lg"
            p={6}
            border="1px solid"
            borderColor="border.default"
          >
            <Text color="text.muted" textAlign="center">
              Aún no hay horarios registrados.
            </Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {grouped.map((group) => (
              <Box
                key={group.weekday}
                bg="bg.card"
                borderRadius="lg"
                border="1px solid"
                borderColor="border.default"
                boxShadow="sm"
                p={4}
              >
                <HStack mb={3} spacing={3}>
                  <Badge colorScheme="purple" px={3} py={1} borderRadius="full">
                    {group.label}
                  </Badge>
                </HStack>

                <VStack align="start" spacing={3}>
                  {group.items.map((ev) => {
                    const colorScheme = ev.color || 'accent';
                    return (
                      <Box
                        key={ev.id}
                        w="100%"
                        borderLeft="4px solid"
                        borderLeftColor={`${colorScheme}.500`}
                        pl={3}
                      >
                        <Text fontWeight="semibold" color="text.default">
                          {ev.title}
                        </Text>
                        <HStack
                          mt={1}
                          spacing={4}
                          color="text.muted"
                          fontSize="sm"
                        >
                          {ev.time && (
                            <HStack spacing={1}>
                              <FiClock />
                              <Text>{ev.time}</Text>
                            </HStack>
                          )}
                          {ev.place && (
                            <HStack spacing={1}>
                              <FiMapPin />
                              <Text>{ev.place}</Text>
                            </HStack>
                          )}
                        </HStack>
                        {ev.description && (
                          <Text mt={1} fontSize="sm" color="text.muted">
                            {ev.description}
                          </Text>
                        )}
                        {ev.firstSundayOnly && (
                          <Text mt={1} fontSize="xs" color="purple.600">
                            Solo el primer {group.label.toLowerCase()} de cada
                            mes.
                          </Text>
                        )}
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Container>
    </>
  );
}
